import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthenticationService } from '@core/authentication/authentication.service';
import { UserService } from '@core/services/user.service';
import { KmdMenuLinksService } from '@features/associations/kmd-menu-links.service';
import { CalendarDay } from '@shared/models/dateTypes';
import {
  setError,
  setFulfilled,
  setPending,
  withRequestStatus,
  withSyncToWebStorage,
} from '@shared/stores/store-features';
import { isAssociationLogin } from '@shared/utilities/association-role.util';

import { HELP_MANIFEST, newestNewsDate } from '../help.manifest';
import {
  HELP_FEATURE_PERMISSIONS,
  HelpArticleMeta,
  HelpAudience,
  HelpFeature,
  HelpSection,
} from '../models';
import { HelpContentService } from '../services/help-content.service';
import {
  articleKey,
  permissionsInManifest,
  permissionsInMarkdown,
  stripPermissionBlocks,
  toSearchText,
  visibleSections,
} from '../utils/help.utils';

/** Every key `loadAccess()` asks: the manifest's, and the ones the features depend on. */
const ACCESS_PERMISSIONS = [
  ...new Set([...permissionsInManifest(HELP_MANIFEST), ...Object.values(HELP_FEATURE_PERMISSIONS).filter((p): p is string => !!p)]),
];

interface HelpState {
  /** Which side of the app the signed-in user belongs to; null until `loadAccess()` has run. */
  audience: HelpAudience | null;
  /** Answer per permission key asked so far; empty until `loadAccess()` has run. */
  granted: Record<string, boolean>;
  /** The markdown per article key as written, kept for the session; `null` when the fetch failed. */
  markdown: Record<string, string | null>;
  /**
   * The newest release note this browser has opened, or null. Persisted per browser: it is a
   * reading convenience, not something the server needs to know.
   */
  newsSeenUpTo: CalendarDay | null;
}

/**
 * Root store: which parts of Hjælp the signed-in user may see, and the articles fetched so far.
 *
 * Everything the reader sees is derived from two things — the answers in `granted` and the raw
 * files in `markdown` — so the section list, the per-reader article text and the search index all
 * filter in one place and follow the user together. Access is asked through `UserService.canI`,
 * which caches per key and forgets on sign-out, so `loadAccess()` is cheap to call on every visit.
 * The request status covers that access check — the one thing the page cannot render without.
 */
export const HelpStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withState<HelpState>({ audience: null, granted: {}, markdown: {}, newsSeenUpTo: null }),
  withSyncToWebStorage({
    key: 'abook:help-news-seen',
    type: 'local',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
    filterState: ({ newsSeenUpTo }) => ({ newsSeenUpTo }),
  }),
  withComputed(({ audience, granted }) => {
    const kmdLinks = inject(KmdMenuLinksService);

    return {
      /**
       * Which features are on for this reader (see `HelpFeature`). Each follows the rule the menu
       * itself uses, so a guide to a menu item is shown exactly where the item is: the tenant has
       * the item on for the login, and the role holds the permission behind it. The association
       * side is the organisations' people, the logins the menu asks the rule about.
       */
      features: computed<Record<HelpFeature, boolean>>(() => {
        const associationLogin = audience() === 'association';
        // A feature without a permission (`kmd_links`) follows the tenant's switch alone.
        const permitted = (feature: HelpFeature) => {
          const permission = HELP_FEATURE_PERMISSIONS[feature];
          return !permission || granted()[permission] === true;
        };
        return {
          kmd_menu_links: kmdLinks.showOwnPages(associationLogin) && permitted('kmd_menu_links'),
          kmd_links: kmdLinks.showOwnLinks(associationLogin) && permitted('kmd_links'),
        };
      }),
    };
  }),
  withComputed(({ audience, features, granted, markdown, newsSeenUpTo }) => ({
    /** A release note newer than the last one opened here — shown as "Ny" on the menu item. */
    hasUnseenNews: computed(() => {
      const newest = newestNewsDate();
      return newest !== null && (newsSeenUpTo() ?? '') < newest;
    }),

    /** The manifest as this user may see it, in manifest order. */
    sections: computed(() =>
      visibleSections(
        HELP_MANIFEST,
        (key) => granted()[key] === true,
        audience(),
        (feature) => features()[feature] === true,
      ),
    ),

    /** Each fetched article with the blocks this user may not see removed — what gets rendered and searched. */
    visibleMarkdown: computed(() => {
      const answers = granted();
      const on = features();
      const visible: Record<string, string> = {};

      for (const [key, text] of Object.entries(markdown())) {
        if (text !== null) {
          visible[key] = stripPermissionBlocks(
            text,
            (permission) => answers[permission] === true,
            (feature) => on[feature] === true,
          );
        }
      }

      return visible;
    }),
  })),
  withComputed(({ visibleMarkdown, sections }) => {
    // One render per article: the computed re-runs whenever an article lands, and re-rendering
    // every article through marked each time would grow with the square of the count. Keyed by
    // article, so the map holds at most one entry per article however often the visible text changes.
    const rendered = new Map<string, { text: string; plain: string }>();
    const searchTextOf = (key: string, text: string): string => {
      const cached = rendered.get(key);
      if (cached?.text === text) return cached.plain;

      const plain = toSearchText(text);
      rendered.set(key, { text, plain });
      return plain;
    };

    return {
      /**
       * `<section>/<slug>` of every article this reader may open — what a "?" button can safely
       * link to. Until `loadAccess()` has run it holds only the articles open to everyone, so
       * nothing gated is offered before the answers are in.
       */
      openableKeys: computed(
        () =>
          new Set(
            sections().flatMap((section) =>
              section.articles.map((article) => articleKey(section.slug, article.slug)),
            ),
          ),
      ),

      /** Plain text per article key, for search. */
      searchText: computed(() =>
        Object.fromEntries(
          Object.entries(visibleMarkdown()).map(([key, text]) => [key, searchTextOf(key, text)]),
        ),
      ),
    };
  }),
  withMethods((store) => {
    const userService = inject(UserService);
    const authentication = inject(AuthenticationService);
    const content = inject(HelpContentService);
    const kmdLinks = inject(KmdMenuLinksService);
    /** Fetches under way, so a search started mid-load does not fetch the open article twice. */
    const inFlight = new Map<string, Promise<void>>();

    async function ask(keys: string[]): Promise<Record<string, boolean>> {
      const answers = await Promise.all(keys.map((key) => firstValueFrom(userService.canI(key))));
      return Object.fromEntries(keys.map((key, i) => [key, answers[i]]));
    }

    return {
      /** Asks every key known so far again, so the answers follow a user change in the same tab. */
      async loadAccess(): Promise<void> {
        if (!store.isFulfilled()) {
          patchState(store, setPending());
        }

        try {
          // The subject is seeded with null until the session is settled — same wait as the main menu.
          const user = await firstValueFrom(
            authentication.loggedUserSubject.pipe(filter((u) => u?.primaryRole !== undefined)),
          );
          const association = isAssociationLogin(user?.primaryRole);
          const audience: HelpAudience = association ? 'association' : 'admin';
          const keys = [...new Set([...ACCESS_PERMISSIONS, ...Object.keys(store.granted())])];
          // The tenant's KMD settings are what a guide to the KMD pages depends on: wait for them
          // too, so an address to that guide is not "not found" for a moment before they arrive.
          // Only an association login has such a guide, so nobody else waits on that endpoint.
          const [answers] = await Promise.all([
            ask(keys),
            association ? kmdLinks.load() : Promise.resolve(),
          ]);
          // Merge, not replace: an article fetched meanwhile may have asked about keys of its own.
          patchState(
            store,
            { audience, granted: { ...store.granted(), ...answers } },
            setFulfilled(),
          );
        } catch (error) {
          patchState(
            store,
            setError(error instanceof Error ? error.message : 'Kunne ikke hente rettigheder'),
          );
        }
      },

      /** Fetches an article once; a failed fetch is retried on the next call. */
      loadArticle(section: HelpSection, article: HelpArticleMeta): Promise<void> {
        const key = articleKey(section.slug, article.slug);

        if (typeof store.markdown()[key] === 'string') return Promise.resolve();

        const running = inFlight.get(key);
        if (running) return running;

        const task = (async () => {
          try {
            const text = await firstValueFrom(content.getArticle(section, article));
            // A block may gate on a key the manifest never mentions — ask before the text is shown.
            const unknown = permissionsInMarkdown(text).filter(
              (permission) => store.granted()[permission] === undefined,
            );

            if (unknown.length > 0) {
              patchState(store, { granted: { ...store.granted(), ...(await ask(unknown)) } });
            }

            patchState(store, { markdown: { ...store.markdown(), [key]: text } });
          } catch {
            patchState(store, { markdown: { ...store.markdown(), [key]: null } });
          } finally {
            inFlight.delete(key);
          }
        })();

        inFlight.set(key, task);

        return task;
      },

      /** The reader opened a release note: nothing up to that day counts as new any more. */
      markNewsSeen(date: CalendarDay): void {
        if ((store.newsSeenUpTo() ?? '') < date) {
          patchState(store, { newsSeenUpTo: date });
        }
      },

      /** Fetches every visible article not yet fetched — what a search over the bodies needs. */
      async loadAllVisible(): Promise<void> {
        await Promise.all(
          store
            .sections()
            .flatMap((section) =>
              section.articles.map((article) => this.loadArticle(section, article)),
            ),
        );
      },
    };
  }),
);
